import { useState, useMemo } from 'react';
import { FileText, Plus, Trash2, Pencil, Download, X, Check, Search, Package } from 'lucide-react';
import Button from '@/components/ui/Button';
import Input from '@/components/ui/Input';
import Modal from '@/components/ui/Modal';
import { Budget, BudgetStatus, Product } from '@/types';
import { useBudgets, useCreateBudget, useUpdateBudget, useDeleteBudget } from '@/hooks/useBudgets';
import { useOwners } from '@/hooks/useOwners';
import { useProducts } from '@/hooks/useProducts';
import { useAuthStore } from '@/context/authStore';
import { formatPrice } from '@/utils/format';
import { generateBudgetPdf } from '@/services/pdfService';
import toast from 'react-hot-toast';

interface ItemRow {
  product_id?: number;
  description: string;
  quantity: string;
  unit_price: string;
}

const num = (v: string): number => {
  const n = parseFloat((v || '').replace(',', '.'));
  return isNaN(n) ? 0 : n;
};

const STATUS_LABELS: Record<BudgetStatus, string> = {
  pending: 'Pendiente',
  accepted: 'Aceptado',
  rejected: 'Rechazado',
};
const STATUS_STYLES: Record<BudgetStatus, string> = {
  pending: 'bg-amber-100 text-amber-700',
  accepted: 'bg-green-100 text-green-700',
  rejected: 'bg-red-100 text-red-700',
};

export default function Budgets() {
  const tenant = useAuthStore((s) => s.tenant);
  const { data: budgets = [], isLoading } = useBudgets();
  const { data: owners = [] } = useOwners();
  const { data: products = [] } = useProducts({ only_active: true });

  const createBudget = useCreateBudget();
  const updateBudget = useUpdateBudget();
  const deleteBudget = useDeleteBudget();

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editing, setEditing] = useState<Budget | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<Budget | null>(null);

  // Form
  const [ownerId, setOwnerId] = useState<string>('');
  const [clientName, setClientName] = useState('');
  const [clientPhone, setClientPhone] = useState('');
  const [validDays, setValidDays] = useState('15');
  const [discount, setDiscount] = useState('');
  const [notes, setNotes] = useState('');
  const [items, setItems] = useState<ItemRow[]>([]);

  const productById = useMemo(() => {
    const m = new Map<number, (typeof products)[number]>();
    products.forEach((p) => m.set(p.id, p));
    return m;
  }, [products]);

  const subtotal = useMemo(
    () => items.reduce((sum, it) => sum + num(it.quantity) * num(it.unit_price), 0),
    [items]
  );
  const total = Math.max(0, subtotal - num(discount));

  const resetForm = () => {
    setEditing(null);
    setOwnerId('');
    setClientName('');
    setClientPhone('');
    setValidDays('15');
    setDiscount('');
    setNotes('');
    setItems([]);
  };

  const openCreate = () => {
    resetForm();
    setItems([{ description: '', quantity: '1', unit_price: '' }]);
    setIsModalOpen(true);
  };

  const openEdit = (b: Budget) => {
    setEditing(b);
    setOwnerId(b.owner_id ? String(b.owner_id) : '');
    setClientName(b.client_name ?? '');
    setClientPhone(b.client_phone ?? '');
    setValidDays(b.valid_days != null ? String(b.valid_days) : '');
    setDiscount(b.discount_amount ? String(Number(b.discount_amount)) : '');
    setNotes(b.notes ?? '');
    setItems(
      (b.items || []).map((it) => ({
        product_id: it.product_id,
        description: it.description,
        quantity: String(Number(it.quantity)),
        unit_price: String(Number(it.unit_price)),
      }))
    );
    setIsModalOpen(true);
  };

  const onSelectOwner = (id: string) => {
    setOwnerId(id);
    if (id) {
      const o = owners.find((ow) => ow.id === parseInt(id));
      if (o) {
        setClientName(`${o.first_name} ${o.last_name}`.trim());
        setClientPhone(o.phone ?? '');
      }
    }
  };

  const addItem = () => setItems((its) => [...its, { description: '', quantity: '1', unit_price: '' }]);
  const removeItem = (idx: number) => setItems((its) => its.filter((_, i) => i !== idx));
  const updateItem = (idx: number, patch: Partial<ItemRow>) =>
    setItems((its) => its.map((it, i) => (i === idx ? { ...it, ...patch } : it)));

  const onSelectProduct = (idx: number, p: Product) => {
    updateItem(idx, {
      product_id: p.id,
      description: p.name,
      unit_price: p.sale_price != null ? String(Number(p.sale_price)) : '',
    });
  };

  // Totales en los otros medios de pago (mismo criterio que el backend: se respeta la
  // proporción entre el precio de efectivo y el de tarjeta/cuotas de cada producto; si el
  // ítem es a mano o el producto no tiene precio de tarjeta, se usa el recargo de la vete).
  const paymentTotals = useMemo(() => {
    const surchargeFactor = 1 + Number(tenant?.card_surcharge_percent ?? 0) / 100;
    const byPaymentMethod = !!tenant?.price_by_payment_method;
    let subCard = 0;
    let subInst = 0;
    items.forEach((it) => {
      const qty = num(it.quantity);
      const cash = num(it.unit_price);
      const p = it.product_id ? productById.get(it.product_id) : undefined;
      const base = Number(p?.sale_price ?? 0);
      const cardPrice = Number(p?.list_price ?? 0);
      const instPrice = Number(p?.installment_price ?? 0);
      const cardFactor = base > 0 && cardPrice > 0 ? cardPrice / base : surchargeFactor;
      const instFactor = byPaymentMethod && base > 0 && instPrice > 0 ? instPrice / base : cardFactor;
      subCard += qty * cash * cardFactor;
      subInst += qty * cash * instFactor;
    });
    const d = num(discount);
    return {
      card: Math.max(0, subCard - d),
      installment: byPaymentMethod ? Math.max(0, subInst - d) : null,
    };
  }, [items, discount, productById, tenant]);

  const validItems = items.filter((it) => it.description.trim() && num(it.quantity) > 0);
  const isValid = (clientName.trim().length > 0 || ownerId) && validItems.length > 0;

  const buildPayload = () => ({
    owner_id: ownerId ? parseInt(ownerId) : undefined,
    client_name: clientName.trim() || undefined,
    client_phone: clientPhone.trim() || undefined,
    valid_days: validDays ? parseInt(validDays) : undefined,
    notes: notes.trim() || undefined,
    discount_amount: num(discount) > 0 ? num(discount) : 0,
    items: validItems.map((it) => ({
      product_id: it.product_id,
      description: it.description.trim(),
      quantity: num(it.quantity),
      unit_price: num(it.unit_price),
    })),
  });

  const handleSave = async () => {
    if (!isValid) return;
    try {
      if (editing) {
        await updateBudget.mutateAsync({ id: editing.id, data: buildPayload() });
      } else {
        await createBudget.mutateAsync(buildPayload());
      }
      setIsModalOpen(false);
      resetForm();
    } catch {
      /* el hook ya muestra el toast */
    }
  };

  const handleDownload = async (b: Budget) => {
    if (!tenant) {
      toast.error('No se pudo cargar la veterinaria');
      return;
    }
    try {
      await generateBudgetPdf(b, tenant);
    } catch (e) {
      console.error(e);
      toast.error('Error al generar el PDF');
    }
  };

  const handleDelete = async () => {
    if (!deleteConfirm) return;
    try {
      await deleteBudget.mutateAsync(deleteConfirm.id);
      setDeleteConfirm(null);
    } catch {
      /* toast en el hook */
    }
  };

  const handleSetStatus = async (b: Budget, status: BudgetStatus) => {
    try {
      await updateBudget.mutateAsync({ id: b.id, data: { status } });
    } catch {
      /* toast en el hook */
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
            <FileText className="h-6 w-6 text-primary-600" />
            Presupuestos
          </h1>
          <p className="text-gray-500">Armá presupuestos, guardalos y descargalos en PDF.</p>
        </div>
        <Button onClick={openCreate}>
          <Plus className="h-5 w-5 mr-2" />
          Nuevo presupuesto
        </Button>
      </div>

      {/* Lista */}
      {isLoading ? (
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-gray-600 mx-auto" />
        </div>
      ) : budgets.length === 0 ? (
        <div className="bg-white rounded-2xl border border-gray-200 text-center py-16">
          <FileText className="h-16 w-16 text-gray-300 mx-auto mb-4" />
          <p className="text-gray-600 font-medium">No hay presupuestos</p>
          <p className="text-gray-400 text-sm">Creá el primero con "Nuevo presupuesto"</p>
        </div>
      ) : (
        <div className="bg-white rounded-2xl border border-gray-200 divide-y divide-gray-100">
          {budgets.map((b) => (
            <div key={b.id} className="flex items-center justify-between gap-3 px-4 py-3 flex-wrap">
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <span className="font-semibold text-gray-800">{b.budget_number || `#${b.id}`}</span>
                  <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${STATUS_STYLES[b.status]}`}>
                    {STATUS_LABELS[b.status]}
                  </span>
                </div>
                <p className="text-sm text-gray-600 truncate">
                  {b.client_name || b.owner_name || 'Sin cliente'}
                  {b.valid_until ? ` · vence ${new Date(b.valid_until).toLocaleDateString('es-AR')}` : ''}
                </p>
              </div>
              <div className="flex items-center gap-3">
                <span className="text-lg font-bold text-green-600">{formatPrice(b.total)}</span>
                <div className="flex items-center gap-1.5">
                  {b.status === 'pending' && (
                    <>
                      <button onClick={() => handleSetStatus(b, 'accepted')} title="Marcar aceptado"
                        className="p-2 rounded-lg bg-green-50 text-green-600 hover:bg-green-100">
                        <Check className="h-4 w-4" />
                      </button>
                      <button onClick={() => handleSetStatus(b, 'rejected')} title="Marcar rechazado"
                        className="p-2 rounded-lg bg-red-50 text-red-500 hover:bg-red-100">
                        <X className="h-4 w-4" />
                      </button>
                    </>
                  )}
                  <button onClick={() => handleDownload(b)} title="Descargar PDF"
                    className="p-2 rounded-lg bg-primary-50 text-primary-600 hover:bg-primary-100">
                    <Download className="h-4 w-4" />
                  </button>
                  <button onClick={() => openEdit(b)} title="Editar"
                    className="p-2 rounded-lg bg-gray-100 hover:bg-gray-200 text-gray-700">
                    <Pencil className="h-4 w-4" />
                  </button>
                  <button onClick={() => setDeleteConfirm(b)} title="Eliminar"
                    className="p-2 rounded-lg bg-red-50 text-red-500 hover:bg-red-100">
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Modal crear/editar */}
      <Modal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)}
        title={editing ? `Editar ${editing.budget_number || 'presupuesto'}` : 'Nuevo presupuesto'} size="xl">
        <div className="space-y-4">
          {/* Cliente */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Cliente (de la lista)</label>
              <select value={ownerId} onChange={(e) => onSelectOwner(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white">
                <option value="">— Sin cliente / a mano —</option>
                {owners.map((o) => (
                  <option key={o.id} value={o.id}>{o.first_name} {o.last_name}</option>
                ))}
              </select>
            </div>
            <Input label="Nombre del cliente" placeholder="Escribí a mano si no está en la lista"
              value={clientName} onChange={(e) => setClientName(e.target.value)} />
            <Input label="Teléfono" placeholder="Opcional"
              value={clientPhone} onChange={(e) => setClientPhone(e.target.value)} />
          </div>

          {/* Items */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-sm font-medium text-gray-700">Ítems</label>
              <button type="button" onClick={addItem}
                className="text-sm text-primary-600 hover:text-primary-800 flex items-center gap-1">
                <Plus className="h-4 w-4" /> Agregar ítem
              </button>
            </div>
            <div className="space-y-2">
              {items.map((it, idx) => (
                <div key={idx} className="grid grid-cols-12 gap-2 items-start">
                  <div className="col-span-12 md:col-span-5">
                    <ProductPicker
                      products={products}
                      selected={it.product_id ? productById.get(it.product_id) : undefined}
                      onSelect={(p) => onSelectProduct(idx, p)}
                      onClear={() => updateItem(idx, { product_id: undefined })}
                    />
                    <input type="text" placeholder="Descripción" value={it.description}
                      onChange={(e) => updateItem(idx, { description: e.target.value })}
                      className="w-full px-2 py-2 border border-gray-300 rounded-lg text-sm" />
                  </div>
                  <div className="col-span-3 md:col-span-2">
                    <input type="number" step="any" min="0" placeholder="Cant." value={it.quantity}
                      onChange={(e) => updateItem(idx, { quantity: e.target.value })}
                      className="w-full px-2 py-2 border border-gray-300 rounded-lg text-sm" />
                  </div>
                  <div className="col-span-4 md:col-span-2">
                    <input type="number" step="any" min="0" placeholder="P. unit." value={it.unit_price}
                      onChange={(e) => updateItem(idx, { unit_price: e.target.value })}
                      className="w-full px-2 py-2 border border-gray-300 rounded-lg text-sm" />
                  </div>
                  <div className="col-span-4 md:col-span-2 text-right text-sm font-medium text-gray-700 py-2">
                    {formatPrice(num(it.quantity) * num(it.unit_price))}
                  </div>
                  <div className="col-span-1 flex justify-end">
                    <button type="button" onClick={() => removeItem(idx)}
                      className="p-2 rounded-lg bg-red-50 text-red-500 hover:bg-red-100">
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Validez / descuento */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Válido por (días)</label>
              <input type="number" min="0" step="1" placeholder="15" value={validDays}
                onChange={(e) => setValidDays(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Descuento (monto)</label>
              <input type="number" min="0" step="any" placeholder="0" value={discount}
                onChange={(e) => setDiscount(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" />
            </div>
          </div>

          {/* Notas adicionales (salen al final del PDF) */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">Notas adicionales</label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={3}
              placeholder="Ej: condiciones, formas de pago, aclaraciones. Sale al final del presupuesto."
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm resize-y"
            />
          </div>

          {/* Totales */}
          <div className="bg-gray-50 border border-gray-200 rounded-xl p-4 space-y-1">
            <div className="flex justify-between text-sm text-gray-600">
              <span>Subtotal</span><span>{formatPrice(subtotal)}</span>
            </div>
            {num(discount) > 0 && (
              <div className="flex justify-between text-sm text-red-600">
                <span>Descuento</span><span>−{formatPrice(num(discount))}</span>
              </div>
            )}
            <div className="flex justify-between text-base font-bold text-gray-900 border-t pt-1 mt-1">
              <span>Total en efectivo</span><span className="text-green-600">{formatPrice(total)}</span>
            </div>
            {Math.round(paymentTotals.card) !== Math.round(total) && (
              <div className="flex justify-between text-sm text-gray-700">
                <span>Tarjeta / transferencia</span><span className="font-medium">{formatPrice(paymentTotals.card)}</span>
              </div>
            )}
            {paymentTotals.installment != null &&
              Math.round(paymentTotals.installment) !== Math.round(paymentTotals.card) && (
                <div className="flex justify-between text-sm text-gray-700">
                  <span>Crédito en cuotas</span>
                  <span className="font-medium">{formatPrice(paymentTotals.installment)}</span>
                </div>
              )}
            <p className="text-xs text-gray-400 pt-1">Los tres importes salen impresos en el PDF.</p>
          </div>

          <div className="flex gap-3 pt-1">
            <Button variant="secondary" className="flex-1" onClick={() => setIsModalOpen(false)}>Cancelar</Button>
            <Button className="flex-1" disabled={!isValid || createBudget.isPending || updateBudget.isPending}
              onClick={handleSave}>
              <Check className="h-5 w-5 mr-2" />
              {editing ? 'Guardar cambios' : 'Crear presupuesto'}
            </Button>
          </div>
        </div>
      </Modal>

      {/* Modal eliminar */}
      <Modal isOpen={!!deleteConfirm} onClose={() => setDeleteConfirm(null)} title="Eliminar presupuesto">
        <div className="space-y-4">
          <p>¿Eliminar el presupuesto <strong>{deleteConfirm?.budget_number}</strong>?</p>
          <div className="flex gap-3">
            <Button variant="secondary" className="flex-1" onClick={() => setDeleteConfirm(null)}>No</Button>
            <Button variant="danger" className="flex-1" onClick={handleDelete} disabled={deleteBudget.isPending}>
              Sí, eliminar
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}

/**
 * Buscador de productos para cada ítem del presupuesto (reemplaza al desplegable:
 * con catálogos grandes buscar por nombre o código de barras es mucho más rápido).
 */
function ProductPicker({
  products,
  selected,
  onSelect,
  onClear,
}: {
  products: Product[];
  selected?: Product;
  onSelect: (p: Product) => void;
  onClear: () => void;
}) {
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);

  const results = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (q.length < 2) return [];
    return products
      .filter((p) => p.name.toLowerCase().includes(q) || (p.barcode || '').toLowerCase().includes(q))
      .slice(0, 8);
  }, [products, query]);

  if (selected) {
    return (
      <div className="flex items-center gap-2 px-2 py-2 mb-1 border border-primary-200 bg-primary-50 rounded-lg">
        <Package className="h-4 w-4 text-primary-600 flex-shrink-0" />
        <span className="text-sm text-gray-800 break-words leading-snug flex-1">{selected.name}</span>
        <button type="button" title="Quitar producto"
          onClick={() => { onClear(); setQuery(''); }}
          className="p-1 rounded text-gray-500 hover:bg-primary-100">
          <X className="h-3.5 w-3.5" />
        </button>
      </div>
    );
  }

  return (
    <div className="relative mb-1">
      <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-gray-400 pointer-events-none" />
      <input
        type="text"
        value={query}
        onChange={(e) => { setQuery(e.target.value); setOpen(true); }}
        onFocus={() => setOpen(true)}
        onBlur={() => setTimeout(() => setOpen(false), 150)}
        placeholder="Buscar producto (opcional)"
        className="w-full pl-8 pr-2 py-2 border border-gray-300 rounded-lg text-sm"
      />
      {open && query.trim().length >= 2 && (
        <div className="absolute z-30 left-0 mt-1 w-full min-w-[26rem] max-w-[36rem] bg-white border border-gray-200 rounded-lg shadow-lg max-h-72 overflow-y-auto">
          {results.length === 0 ? (
            <p className="px-3 py-2 text-sm text-gray-400">Sin resultados para "{query.trim()}"</p>
          ) : (
            results.map((p) => (
              <button
                key={p.id}
                type="button"
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => { onSelect(p); setQuery(''); setOpen(false); }}
                className="w-full flex items-center justify-between gap-2 px-3 py-2 text-left hover:bg-gray-50"
              >
                <span className="text-sm text-gray-800 break-words leading-snug">{p.name}</span>
                {p.sale_price != null && (
                  <span className="text-xs text-gray-500 flex-shrink-0">{formatPrice(p.sale_price)}</span>
                )}
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
}
